import { toast } from "@/components/ui";
import {
  AVAILABLE_ACTIONS_QUERY_KEYS,
  CHAT_QUERY_KEYS,
  QueryKeys,
} from "@/constants/Query";
import type { PatientTabsResponse } from "@/features/PatientTabs/network/usePatientTabs";
import { useNavigateLiveQueue } from "@/hooks/useNavigateLiveQueue";
import { formatDateTime } from "@/lib/time";
import { wildcardInvalidateQuery } from "@/lib/utils";
import { useConfigCatFeatureFlag } from "@/network/useConfigCatFeatureFlag";
import { useUser } from "@/network/useUser";
import { socketsAtom } from "@/store/socketsAtom";
import {
  MedicationRequest,
  Observation,
  RequestGroup,
  ServiceRequest,
} from "@curai/js-fhir-tools";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams, useSearch } from "@tanstack/react-router";
import { useAtomValue } from "jotai";
import { useEffect } from "react";

export interface LabOrderConfirmedAndPaidData {
  patientId: string;
  childEncounterId: string;
  requestGroupId: string;
  serviceRequestIds: string[];
}

export interface LabOrderCancelledData {
  patientId: string;
  childEncounterId: string;
  requestGroupId: string;
  serviceRequestIds: string[];
}

export interface SubjectiveAddendumAdded {
  patientId: string;
  encounterId: string;
  patientName: string;
}

export interface AVCallConditionsReady {
  patientId: string;
  encounterId: string;
  patientName: string;
  historicalConditionsCount: number;
}

export interface AssessmentGenerated {
  patientId: string;
  encounterId: string;
  patientName: string;
  generationResult: "SUCCESS" | "FAILED";
  workflowId?: string;
}

export type AiFeedEventType =
  | "acceptance"
  | "rejection"
  | "expiration"
  | "addition";

const CREATE_CONDITION_INTENT_TYPE = "create_condition";
const AI_FEED_SUGGESTION_TOAST_TITLE = "AI Suggestions";
const AI_FEED_SUGGESTION_TOAST_DESCRIPTION =
  "New charting suggestions have been added to the AI Feed";

export interface AiFeedUpdated {
  patientId: string;
  encounterId: string;
  aiFeedEventType: AiFeedEventType;
  aiFeedItemId?: number;
  intentType?: string;
  requester?: number | null;
}

export interface AbandonedVisit {
  patientId: string;
  encounterId: string;
  patientName: string;
  timestamp: string;
}

export interface PatientChartUpdatedResources {
  medicationRequest?: MedicationRequest[];
  observation?: Observation[];
  serviceRequest?: ServiceRequest[];
  requestGroup?: RequestGroup[];
}
export interface PatientChartUpdatedData {
  requester?: string;
  resources: PatientChartUpdatedResources;
}

export interface EncounterStolenData {
  patientId: string;
  encounterId: string;
  patientName: string;
  stealingProfessionalId: number;
  stealingProfessionalName: string;
}

export const deserializeData = (data: {
  requester?: string;
  resources: Record<string, object[]>;
}): PatientChartUpdatedData => {
  const requester = data.requester;

  const medicationRequests =
    data.resources.medicationRequest?.map((medicationRequest: object) =>
      medicationRequest instanceof MedicationRequest
        ? medicationRequest
        : new MedicationRequest(medicationRequest),
    ) || [];

  const observations =
    data.resources.observation?.map((observation: object) =>
      observation instanceof Observation
        ? observation
        : new Observation(observation),
    ) || [];

  const serviceRequests =
    data.resources.serviceRequest?.map((serviceRequest: object) =>
      serviceRequest instanceof ServiceRequest
        ? serviceRequest
        : new ServiceRequest(serviceRequest),
    ) || [];

  const requestGroups =
    data.resources.requestGroup?.map((requestGroup: object) =>
      requestGroup instanceof RequestGroup
        ? requestGroup
        : new RequestGroup(requestGroup),
    ) || [];

  return {
    requester: requester,
    resources: {
      medicationRequest: medicationRequests,
      observation: observations,
      serviceRequest: serviceRequests,
      requestGroup: requestGroups,
    },
  };
};

export function useChartSocketEvents() {
  const chartingSocket = useAtomValue(socketsAtom)?.charting;
  const queryClient = useQueryClient();
  const navigateLiveQueue = useNavigateLiveQueue();
  const navigate = useNavigate();
  const { patientId: currentPatientId } = useParams({ strict: false });
  const { encounterId } = useSearch({ strict: false });

  const { data: me } = useUser();
  const { data: aiFeedEnabled } = useConfigCatFeatureFlag(
    "ehr-ai-feed-enabled",
  );

  useEffect(() => {
    if (!chartingSocket) return;

    // Gate on open-tab membership (not the focused encounter) so background
    // tabs still notify. Fails closed: a missing cache suppresses the toast.
    const isPatientInOpenTabs = (patientId: string) => {
      const openTabs = queryClient.getQueryData<PatientTabsResponse>(
        QueryKeys.Provider.PATIENT_TABS,
      );
      return Boolean(
        openTabs?.patientTabs.some((tab) => tab.patient.id === patientId),
      );
    };

    const handleLabOrderConfirmedAndPaid = (
      data: LabOrderConfirmedAndPaidData,
    ) => {
      queryClient.invalidateQueries({
        queryKey: QueryKeys.Patient.Chart.Labs.DEFAULT({
          patientId: data.patientId,
          encounterId: data.childEncounterId,
        }),
      });
    };

    const handleLabOrderCancelled = (data: LabOrderCancelledData) => {
      queryClient.invalidateQueries({
        queryKey: QueryKeys.Patient.Chart.Labs.DEFAULT({
          patientId: data.patientId,
          encounterId: data.childEncounterId,
        }),
      });
      queryClient.invalidateQueries({
        queryKey: QueryKeys.Patient.Chart.EpisodesOfCare.CarePlan.SUMMARY({
          patientId: data.patientId,
          encounterId: data.childEncounterId,
        }),
      });
    };

    const handleEncounterStolen = (data: EncounterStolenData) => {
      chartingSocket.emit("refresh", null);
      if (data.stealingProfessionalId === me?.userId) {
        // Current user is the one who stole the encounter
        // Invalidate all available action and charting queries
        for (const queryKey of AVAILABLE_ACTIONS_QUERY_KEYS) {
          wildcardInvalidateQuery(
            queryClient,
            queryKey.filter((key) => key !== undefined),
          );
        }
        for (const queryKey of CHAT_QUERY_KEYS) {
          wildcardInvalidateQuery(
            queryClient,
            queryKey.filter((key) => key !== undefined),
          );
        }
      } else {
        // Someone else stole the encounter
        if (data.encounterId === encounterId?.toString()) {
          // The stolen encounter matches our current encounter
          toast.warning(
            `Patient ${data.patientName} was taken by ${data.stealingProfessionalName}.`,
            {
              duration: Number.POSITIVE_INFINITY,
            },
          );

          // Invalidate available actions
          for (const queryKey of AVAILABLE_ACTIONS_QUERY_KEYS) {
            wildcardInvalidateQuery(
              queryClient,
              queryKey.filter((key) => key !== undefined),
            );
          }

          // Navigate to live-queue
          navigateLiveQueue();
        }
        // If encounterId doesn't match, do nothing
      }
      // Invalidate patient tabs regardless of stealer or stealee
      queryClient.invalidateQueries({
        queryKey: QueryKeys.Provider.PATIENT_TABS,
        type: "active",
      });
    };

    const handleSubjectiveAddendum = (data: SubjectiveAddendumAdded) => {
      queryClient.invalidateQueries({
        queryKey: QueryKeys.Patient.Chart.Subjective.DEFAULT({
          patientId: data.patientId,
          encounterId: data.encounterId,
        }),
      });

      if (!isPatientInOpenTabs(data.patientId)) return;

      const isCurrentPatientEncounter =
        data.patientId === currentPatientId &&
        data.encounterId === encounterId?.toString();

      toast.superclinical("New Subjective Available", {
        description: `A new AI-generated subjective is available for ${data.patientName}.`,
        duration: Number.POSITIVE_INFINITY,
        ...(isCurrentPatientEncounter
          ? {}
          : {
              action: {
                label: "View",
                onClick: () => {
                  navigate({
                    to: "/patient/$patientId/timeline",
                    params: {
                      patientId: data.patientId,
                    },
                    search: {
                      encounterId: data.encounterId,
                    },
                  });
                },
              },
            }),
      });
    };

    const handleAssessmentGenerated = (data: AssessmentGenerated) => {
      queryClient.invalidateQueries({
        queryKey:
          QueryKeys.Patient.Chart.EpisodesOfCare.Assessment.GENERATION_STATUS(
            data.patientId,
            data.encounterId,
          ),
      });

      if (data.generationResult !== "SUCCESS") return;

      queryClient.invalidateQueries({
        queryKey: QueryKeys.Patient.Chart.EpisodesOfCare.Assessment.DEFAULT({
          patientId: data.patientId,
          encounterId: data.encounterId,
        }),
      });

      if (!isPatientInOpenTabs(data.patientId)) return;

      const isCurrentPatientEncounter =
        data.patientId === currentPatientId &&
        data.encounterId === encounterId?.toString();

      toast.superclinical("New Assessment Available", {
        description: `A new AI-generated assessment is available for ${data.patientName}.`,
        duration: Number.POSITIVE_INFINITY,
        ...(isCurrentPatientEncounter
          ? {}
          : {
              action: {
                label: "View",
                onClick: () => {
                  navigate({
                    to: "/patient/$patientId/timeline",
                    params: {
                      patientId: data.patientId,
                    },
                    search: {
                      encounterId: data.encounterId,
                    },
                  });
                },
              },
            }),
      });
    };

    const handleAVCallConditionsReady = (data: AVCallConditionsReady) => {
      // Refresh the suggestions panel immediately so when the provider opens
      // Medical History it's already populated (no manual reload).
      queryClient.invalidateQueries({
        queryKey: QueryKeys.Patient.Chart.AVCallConditions.DEFAULT({
          patientId: data.patientId,
          encounterId: data.encounterId,
        }),
      });

      if (!isPatientInOpenTabs(data.patientId)) return;

      // Replaced with AI Feed
      if (aiFeedEnabled) return;

      // 0-count emits would mean the LLM extracted nothing meaningful —
      // skip the toast in that case so we don't bother the provider for
      // empty results. Backend may emit them so the EMR can still refresh.
      if (data.historicalConditionsCount <= 0) return;

      const suggestionLabel =
        data.historicalConditionsCount === 1 ? "suggestion" : "suggestions";

      toast.superclinical("New Curai Health Suggestions", {
        description: `${data.historicalConditionsCount} new medical history ${suggestionLabel} for ${data.patientName}`,
        duration: Number.POSITIVE_INFINITY,
        action: {
          label: "View",
          onClick: () => {
            navigate({
              to: "/patient/$patientId/medical-history",
              params: {
                patientId: data.patientId,
              },
              search: {
                encounterId: data.encounterId,
              },
            });
          },
        },
      });
    };

    const handleAiFeedUpdated = (data: AiFeedUpdated) => {
      if (!aiFeedEnabled) return;

      const selfInitiated = data.requester && data.requester === me?.userId;
      const currentEncounterId = encounterId?.toString();

      if (!selfInitiated) {
        queryClient.invalidateQueries({
          queryKey: QueryKeys.Patient.MedicalRecords.AiFeed.DEFAULT(
            data.patientId,
            data.encounterId,
          ),
        });
      }

      if (
        data.aiFeedEventType === "addition" &&
        data.patientId === currentPatientId &&
        data.encounterId === currentEncounterId
      ) {
        const toastId = `ai-feed-suggestions-${data.patientId}-${data.encounterId}`;

        toast.superclinical(AI_FEED_SUGGESTION_TOAST_TITLE, {
          id: toastId,
          description: AI_FEED_SUGGESTION_TOAST_DESCRIPTION,
          duration: Number.POSITIVE_INFINITY,
        });
      }

      // Accepting a create-condition item writes a Condition into Medical
      // History, so refresh that view too. Other event types (rejection,
      // expiration, addition) don't change charted resources.
      if (
        data.aiFeedEventType === "acceptance" &&
        data.intentType === CREATE_CONDITION_INTENT_TYPE
      ) {
        queryClient.invalidateQueries({
          queryKey: QueryKeys.Patient.MedicalRecords.MedicalHistory.DEFAULT(
            data.patientId,
          ),
          refetchType: "all",
        });
      }
    };

    const handleAbandonedVisit = (data: AbandonedVisit) => {
      if (!isPatientInOpenTabs(data.patientId)) return;

      const formattedTimestamp = formatDateTime(data.timestamp);
      toast.warning(`${data.patientName} Inactive ${formattedTimestamp}`, {
        description: "Please close visit per workflow.",
        duration: Number.POSITIVE_INFINITY,
        action: {
          label: "View",
          onClick: () => {
            navigate({
              to: "/patient/$patientId/timeline",
              params: {
                patientId: data.patientId,
              },
              search: {
                encounterId: data.encounterId,
              },
            });
          },
        },
      });
    };

    chartingSocket.on(
      "lab_order_confirmed_and_paid",
      handleLabOrderConfirmedAndPaid,
    );
    chartingSocket.on("lab_order_cancelled", handleLabOrderCancelled);

    chartingSocket.on("encounter_stolen", handleEncounterStolen);

    chartingSocket.on("subjective_addendum", handleSubjectiveAddendum);

    chartingSocket.on("assessment_generated", handleAssessmentGenerated);

    chartingSocket.on("av_call_conditions_ready", handleAVCallConditionsReady);

    chartingSocket.on("visit_abandoned", handleAbandonedVisit);

    chartingSocket.on("ai_feed_updated", handleAiFeedUpdated);

    return () => {
      chartingSocket.off(
        "lab_order_confirmed_and_paid",
        handleLabOrderConfirmedAndPaid,
      );
      chartingSocket.off("lab_order_cancelled", handleLabOrderCancelled);

      // React will clean up the listeners after a component mounts (it cleans up the old listeners), but since the
      // functions are created within the useEffect, the listeners will not be cleaned up since the function signature
      // will be different. This is a workaround to ensure the listeners are cleaned up. Moving these out of the useEffect
      // is the probably cleaner way to do this, but this is a quick fix for now.
      for (const listener of chartingSocket.listeners("encounter_stolen")) {
        chartingSocket.off("encounter_stolen", listener);
      }
      for (const listener of chartingSocket.listeners("subjective_addendum")) {
        chartingSocket.off("subjective_addendum", listener);
      }
      for (const listener of chartingSocket.listeners("assessment_generated")) {
        chartingSocket.off("assessment_generated", listener);
      }
      for (const listener of chartingSocket.listeners(
        "av_call_conditions_ready",
      )) {
        chartingSocket.off("av_call_conditions_ready", listener);
      }
      for (const listener of chartingSocket.listeners("visit_abandoned")) {
        chartingSocket.off("visit_abandoned", listener);
      }
      for (const listener of chartingSocket.listeners("ai_feed_updated")) {
        chartingSocket.off("ai_feed_updated", listener);
      }
    };
  }, [
    chartingSocket,
    queryClient,
    me,
    currentPatientId,
    encounterId,
    navigateLiveQueue,
    navigate,
    aiFeedEnabled,
  ]);
}
